<template>
  <div>
    <div id="publication-table"></div>
  </div>
</template>

<script setup>
import getPublicationColumns  from "@/columns/publications.js";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import api from "@/services/axios";
import { useEventBus } from '@vueuse/core'
import { onMounted, onUnmounted } from "vue";

// Ajoutez ce code dans le setup()
const eventBus = useEventBus('publication-updated')

// Écoutez l'événement de rafraîchissement
const unsubscribe = eventBus.on(() => {
  if (table) {
    table.setData()
      .then(() => console.log('Publications table refreshed'))
      .catch(err => console.error('Refresh error:', err))
  }
})

// Nettoyez l'écouteur quand le composant est démonté
onUnmounted(() => {
  unsubscribe()
})

// Définir un événement pour émettre l'ID du groupe sélectionné
const emit = defineEmits(["publication-selected"]);
let table = null;

async function fetchPublications(url, config, params = {}) {
  try {
    const { page, size, sorters, filters } = params;

    const response = await api.get(`/publications`, {
      params: {
        page,
        size,
        sorters: JSON.stringify(sorters),
        filters: JSON.stringify(filters),
      }
    });


    const today = new Date();
    
    // 🛡️ Filtrer les publications avec des données invalides
    const validPublications = response.data.data.filter(pub => {
      if (!pub || !pub._id) {
        console.warn('⚠️ Publication sans ID trouvée, ignorée:', pub);
        return false;
      }
      if (!pub.publicationName) {
        console.warn('⚠️ Publication sans nom trouvée, ignorée:', pub._id);
        return false;
      }
      return true;
    });
    
    const publications = validPublications.map(pub => {
      const startingDate = new Date(pub.startingDate);
      const endDate = new Date(pub.endDate);
      let status = "Coming";
      if (today >= startingDate && today <= endDate) status = "Open";
      else if (today > endDate) status = "Close";

      return {
        ...pub,
        status
      };
    });

    return {
      data: publications,
      last_page: response.data.last_page
    };

  } catch (error) {
    console.error("Erreur lors de la récupération des publications :", error);
    return { data: [], last_page: 1 };
  }


}


async function deletePublication(publicationId) {
  console.log('🗑️ deletePublication called with ID:', publicationId);
  
  if (!confirm("Are you sure you want to delete this publication?")) {
    console.log('🗑️ Delete cancelled by user');
    return;
  }

  try {
    console.log('🗑️ Attempting to delete publication:', publicationId);
    await api.delete(`/publications/${publicationId}`);
    console.log('✅ Publication deleted successfully');
    alert("Publication deleted successfully!");
    table.setPage(1);// recharge la 1ère page (ou current page si tu veux)
  } catch (error) {
    console.error("❌ Error deleting publication:", error.response?.data || error.message);
    alert("An error occurred while deleting the publication.");
  }
}


function createTable(data) {
  try {
    table = new Tabulator("#publication-table", {
      pagination: true,
      paginationMode: "remote",
      paginationSize: 10,
      paginationSizeSelector: [10, 20, 50, 100],
      paginationCounter: "rows",
      ajaxURL: `/publications/`,
      ajaxRequestFunc: fetchPublications,
      ajaxFiltering: true,
      ajaxSorting: true,
      layout: "fitColumns",
      columns: getPublicationColumns(deletePublication),
      // 🛡️ Configurations pour éviter les erreurs de rendu
      virtualDom: true,
      virtualDomBuffer: 10,
      height: "auto", // Hauteur fixe pour éviter les problèmes de redimensionnement
      resizeColumns: false, // Désactiver le redimensionnement automatique des colonnes
      // 🎨 Formatter pour la surbrillance des lignes sélectionnées
      rowFormatter: function(row) {
        const element = row.getElement();
        const data = row.getData();

        if (data.isHighlighted) {
          element.classList.add("row-highlight");
        } else {
          element.classList.remove("row-highlight");
        }
      },
      // 🛡️ Gestion d'erreurs
      ajaxError: function(error) {
        console.error("❌ Erreur AJAX Tabulator:", error);
        return false; // Empêche l'affichage d'erreur par défaut
      },
      renderStarted: function() {
        console.log("🎬 Début du rendu Tabulator");
      },
      renderComplete: function() {
        console.log("✅ Rendu Tabulator terminé");
      }
    });

    // 🛡️ Gestionnaire d'erreurs pour les événements Tabulator
    table.on("renderError", function(error) {
      console.error("❌ Erreur de rendu Tabulator:", error);
    });

    // Ajoutez l'écouteur pour les clics sur une ligne avec protection
    table.on("rowClick", (e, row) => {
      try {
        // Retirer la classe row-highlight de toutes les lignes
        const allRows = table.getRows();
        allRows.forEach(r => {
          const element = r.getElement();
          if (element) {
            element.classList.remove("row-highlight");
          }
          r.update({ isHighlighted: false });
        });

        // Ajouter la classe à la ligne cliquée
        const element = row.getElement();
        if (element) {
          element.classList.add("row-highlight");
        }
        row.update({ isHighlighted: true });

        const rowData = row.getData();
        if (rowData && rowData._id) {
          emit("publication-selected", rowData._id);
        } else {
          console.warn("⚠️ Données de ligne invalides lors du clic");
        }
      } catch (error) {
        console.error("❌ Erreur lors du clic sur une ligne:", error);
      }
    });

  } catch (error) {
    console.error("❌ Erreur lors de la création de la table Tabulator:", error);
  }
}

// Appeler fetchPublications lorsque le composant est monté
onMounted(() => {
  createTable(); // C'est cette ligne qui est nécessaire pour lancer Tabulator
});

</script>

<style scoped>
.icon-download {
  cursor: pointer;
  margin: 0 5px;
  color: #007bff;
}

.icon-download:hover {
  color: #0056b3;
}
</style>

<style>
/* Ligne sélectionnée avec trait de couleur à gauche (style global) */
#publication-table .tabulator-row.row-highlight {
  background-color: #F3E5F5 !important;
  border-left: 5px solid #7B1FA2 !important;
  font-weight: 600 !important;
  box-shadow: inset 0 0 10px rgba(123, 31, 162, 0.1);
}

#publication-table .tabulator-row.row-highlight:hover {
  background-color: #E1BEE7 !important;
}
</style>
